import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { listAcademicYears, listAcademicYearsForInstitution } from "@/modules/superAdmin/academicYears/service/academicYearService";
import type { RootState } from "@/store";
import type { AcademicYear } from "@/modules/superAdmin/academicYears/types";
import i18n from "@/i18n";

/**
 * Single shared "which academic year am I looking at" state for the whole
 * Institution Admin panel — one dropdown in the header
 * (AcademicYearSelector) drives every year-aware page (Students, Fees,
 * Attendance, Subjects, Staff Salary/Assignments) instead of each one
 * independently fetching years and picking its own default. Switching the
 * header dropdown re-scopes whatever page you're currently on.
 */

export const loadAcademicYearsShared = createAsyncThunk(
  "sharedAcademicYear/load",
  async (_, { getState }) => {
    // A higher-level admin previewing a school (?viewInstitutionId=) sees that school's years; a school login sees its own.
    const viewId = Number(new URLSearchParams(window.location.search).get("viewInstitutionId")) || (getState() as RootState).auth.user?.institutionId;
    if (!viewId) return await listAcademicYears();
    const rows = await listAcademicYearsForInstitution(viewId);
    return rows
      .filter((y) => y.configured && y.id !== null && y.startDate && y.endDate)
      .map((y) => ({ id: y.id as number, label: y.label, startDate: y.startDate as string, endDate: y.endDate as string, isCurrent: y.isCurrent }));
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as { sharedAcademicYear: SharedAcademicYearState };
      const { years, loading } = state.sharedAcademicYear;
      return years.length === 0 && !loading;
    },
  }
);

interface SharedAcademicYearState {
  years: AcademicYear[];
  selectedYearId: number | null;
  loading: boolean;
  error: string | null;
}

const initialState: SharedAcademicYearState = {
  years: [],
  selectedYearId: null,
  loading: false,
  error: null,
};

const sharedAcademicYearSlice = createSlice({
  name: "sharedAcademicYear",
  initialState,
  reducers: {
    setSelectedAcademicYearId: (state, action: { payload: number }) => {
      state.selectedYearId = action.payload;
    },
    /** The school's years changed (dates, current year): forget the list so the header and pages load it again. */
    invalidateAcademicYears: (state) => {
      state.years = [];
      state.selectedYearId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAcademicYearsShared.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadAcademicYearsShared.fulfilled, (state, action) => {
        state.loading = false;
        state.years = action.payload;
        // Only pick a default the first time years load (selectedYearId is
        // still null) — a page re-triggering this load later (e.g. on
        // remount) must never stomp on a year the user already switched to.
        if (state.selectedYearId === null) {
          const current = action.payload.find((y) => y.isCurrent) ?? action.payload[0];
          if (current) state.selectedYearId = current.id;
        }
      })
      .addCase(loadAcademicYearsShared.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? i18n.t("shared:academicYearSlicetsMsgs.failedToLoadAcademicYears");
      });
  },
});

export const { setSelectedAcademicYearId, invalidateAcademicYears } = sharedAcademicYearSlice.actions;
export default sharedAcademicYearSlice.reducer;
